import {
  returnNormalAnchorFillColor,
  returnSelectedAnchorFillColor,
  returnAnchorBorderColor,
} from '@/map-core';
import { AnchorLayerType } from '@/utils/constant';
import { useEditorStore } from '../../store/editorStore';
import { useSearchStore } from '../../store/searchStore';
import { useDataStore } from '../../store/dataStore';
import type { IControllerBridge, IInteractionStrategy } from './types';

/**
 * 浏览模式策略
 * 默认模式：允许选中/取消选中多边形、响应 moveend 拉取数据
 */
export class ViewStrategy implements IInteractionStrategy {
  onSelect(
    ctrl: IControllerBridge,
    feature: any,
    id: number,
    type: 'focus' | 'blur'
  ): void {
    const { variableList } = useDataStore.getState();
    const data = feature?.data
      ? { ...feature.data, coordinates: feature.coordinates }
      : variableList?.find((item: any) => item.id === id);
    if (!data) return;

    // 轻图测试等存在子类型的区域，需要把 subType 透传给样式函数，否则都会回退到主类型默认色
    const subType = data.subType;
    const strokeColor = returnAnchorBorderColor(data.type, subType);
    const strokeObj = { color: strokeColor, width: 2 };

    if (type === 'focus') {
      const color = returnSelectedAnchorFillColor(data.type, subType);
      ctrl.handlePolygonPopup(data);
      useEditorStore.getState().setViewMode(data, feature);
      ctrl.delLaneFeature();
      ctrl.map.changeMapCenter(feature.getGeometry().getFlatInteriorPoint());
      if (data.type === AnchorLayerType.SPEED_LIMIT) {
        // LaneManager 由 Controller 协调，这里通过 ctrl 间接调用
        // 实际调用在 Controller.handleSelect 中补充
      }
      feature.setStyle({ fill: { color }, stroke: strokeObj });
    } else {
      const color = returnNormalAnchorFillColor(data.type, subType);
      ctrl.resetInteractionState();
      useEditorStore.getState().setBrowseMode();
      feature.setStyle({ fill: { color }, stroke: strokeObj });
    }
  }

  onDrawComplete(): void {
    // 浏览模式下不应触发绘制完成
  }

  async onMoveEnd(ctrl: IControllerBridge): Promise<void> {
    const { session } = useEditorStore.getState();
    const selectedId = session.activePolygonData?.id ?? null;
    const hasSelection = selectedId !== null && selectedId !== undefined;

    // 没有选中面时，moveend 前清理普通交互态即可；
    // 有选中面时不要先清弹窗/右侧选中态，否则 list 请求期间会出现“消失一下”的闪烁。
    if (!hasSelection) {
      ctrl.resetInteractionState();
      useEditorStore.getState().setBrowseMode();
    }

    const { positionSearchVal } = useSearchStore.getState();
    const rightSearchVal = useSearchStore.getState().rightSearchVal;

    // fetchList 完成后检查：上一次选中的面是否还在 list 中
    // 如果 fetchList 返回 undefined（去重跳过或失败），保留现有选中态，避免无意义闪烁。
    const processedData = await ctrl.fetchRecordList(
      positionSearchVal,
      rightSearchVal
    );

    if (!hasSelection || !processedData) {
      return;
    }

    const currentSelectedId = useEditorStore.getState().session.activePolygonData?.id ?? null;
    if (currentSelectedId !== selectedId) {
      // 请求期间用户已经切换/关闭选中态，旧请求不再覆盖新的交互结果。
      return;
    }

    const selectedData = processedData.find(
      (item: any) => item.id === selectedId
    );

    if (selectedData) {
      // 数据刷新后恢复选中态：不高亮触发 changeMapCenter，避免地图被拽回中心。
      // 同 id 弹窗会复用原 DOM，只刷新数据和锚点位置。
      ctrl.restoreSelection(selectedData);
      return;
    }

    // 选中的面已经不在新列表范围内，此时才清除详情面板和右侧高亮。
    ctrl.resetInteractionState();
    useEditorStore.getState().setBrowseMode();
  }
}
